<template lang="pug">
//- Reusable Organization(s) selector with optional self-loading and auto-select
v-autocomplete(
  @change="handleChange"
  :clearable="clearable"
  :disabled="disabled"
  :dense="dense"
  :items="items"
  item-text="organization_name"
  item-value="organization_id"
  :loading="isLoading"
  :label="label"
  :multiple="multiple"
  :outlined="outlined"
  :hide-details="hideDetails"
  :prepend-icon="prependIcon"
  :required="required"
  :menu-props="{offsetY: true}"
  v-model="internalValue"
)
</template>

<script lang="ts">
import Vue from 'vue';

// Helpers
import { autoSelectNonSandboxOrganization } from '@/helpers/action';

export default Vue.extend({
  data() {
    return {
      propertyName: 'organization_id',
      items: [] as any[],
      internalValue: null as any,
    };
  },
  methods: {
    normalizeModel(value: any) {
      if (this.multiple) {
        const arr = Array.isArray(value) ? value : [];
        return this.returnNumbers ? arr.map(v => Number(v)) : arr;
      }
      if (value === null || value === undefined || value === '') return null;
      return this.returnNumbers ? Number(value) : value;
    },
    autoselectNonSandboxOrganization() {
      this.ensureAutoSelect();
    },
    setValue() {
      this.emitValue();
    },
    emitValue() {
      this.$emit('input', this.internalValue);
      this.$emit('change', this.internalValue);
    },
    ensureAutoSelect() {
      if (!this.autoSelect) return;
      const hasList = Array.isArray(this.items) && this.items.length > 0;
      let emptySelection = this.multiple
        ? !(Array.isArray(this.internalValue) && this.internalValue.length > 0)
        : !(Number(this.internalValue) > 0);

      if (hasList && !emptySelection) {
        const idSet = new Set(this.items.map((it: any) => Number(it?.organization_id)));
        let valid = false;
        if (this.multiple) {
          valid = (this.internalValue as any[]).every(v => idSet.has(Number(v)));
        } else {
          valid = idSet.has(Number(this.internalValue));
        }
        if (!valid) {
          emptySelection = true;
        }
      }

      if (hasList && emptySelection) {
        // Filter out header objects (used for grouping) before passing to auto-select helper
        // Headers have 'header' property but no organization_id
        const realOrgs = this.items.filter((it: any) => 
          it.organization_id !== undefined && it.organization_id !== null && !it.header
        );
        
        const target: any = { organization_id: null };
        autoSelectNonSandboxOrganization(realOrgs, target, 'organization_id');

        if (target.organization_id) {
          const selected = Number(target.organization_id);
          this.internalValue = this.multiple ? [selected] : selected;
          this.$nextTick(this.emitValue);
        }
      }
    },
    handleChange() {
      this.internalValue = this.normalizeModel(this.internalValue);
      this.emitValue();
    },
  },
  props: {
    autoSelect: {
      type: Boolean,
      default: true,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hideDetails: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: 'Organization(s)',
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    organizationUsers: {
      type: Array,
      default: null,
      required: true,
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    prependIcon: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    returnNumbers: {
      type: Boolean,
      default: true,
    },
    value: {
      type: [Number, Array],
      default: null,
      required: false,
    },
  },
  watch: {
    value(next) {
      this.internalValue = this.normalizeModel(next);
    },
    organizationUsers(next: any[]) {
      if (Array.isArray(next)) {
        this.items = next;
        if (next.length > 0) {
          this.$nextTick(this.ensureAutoSelect);
        }
      }
    },
  },
  created() {
    this.internalValue = this.normalizeModel(this.value);
    if (Array.isArray(this.organizationUsers) && this.organizationUsers.length > 0) {
      this.items = this.organizationUsers;
    }
  },
  mounted() {
    // Ensure auto-select runs after component is mounted, even if organizationUsers was set before mount
    if (Array.isArray(this.organizationUsers) && this.organizationUsers.length > 0) {
      this.$nextTick(this.ensureAutoSelect);
    }
  },
});
</script>

<style lang="scss" scoped>
</style>
